"use client";

import { useReducedMotion } from "motion/react";
import type { CSSProperties, ReactNode } from "react";
import { cn } from "../../lib";

/** Default full bob cycle; theme `--pulse-float-duration` matches when unset. */
export const PULSE_FLOAT_DEFAULT_DURATION_S = 1.5;

interface PulseFloatMotionProps {
	children: ReactNode;
	className?: string;
	/** Custom full bob cycle in seconds. Applies inline when set (theme utility ignores per-instance duration). */
	durationSec?: number;
	/** Seconds to offset the animation start (desyncs multiple floaters). */
	delaySec?: number;
}

/**
 * Repeating ease-in-out vertical float (`animate-pulse-float`). Wrap content whose positioning
 * transform stays on an ancestor so `translateY` here is not overwritten.
 */
export function PulseFloatMotion({
	children,
	className,
	durationSec,
	delaySec = 0,
}: PulseFloatMotionProps) {
	const reducedMotion = useReducedMotion();
	const useCustomDuration = durationSec != null && !reducedMotion;
	const style: CSSProperties | undefined = useCustomDuration
		? {
				animation: `pulse-float ${durationSec}s ease-in-out infinite`,
				animationDelay: `${delaySec}s`,
			}
		: durationSec == null
			? ({
					"--pulse-float-duration": `${PULSE_FLOAT_DEFAULT_DURATION_S}s`,
				} as CSSProperties)
			: undefined;

	return (
		<div
			className={cn(
				"motion-safe:will-change-transform motion-reduce:animate-none",
				!useCustomDuration &&
					durationSec == null &&
					"motion-safe:animate-pulse-float",
				className,
			)}
			style={style}
		>
			{children}
		</div>
	);
}
